import React, { useState, useEffect } from 'react';
import { Calendar, AlertCircle, Clock, CheckCircle2, ChevronRight } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../lib/utils';

export default function FinancialDeadlinesWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDeadlines();
  }, []);

  const fetchDeadlines = async () => {
    try {
      const data = await apiRequest('/bookkeeper/deadlines');
      setDeadlines(data);
    } catch (error) {
      // Comprehensive Australian tax deadlines with demo data
      const today = new Date();
      const currentYear = today.getFullYear();
      
      setDeadlines([
        // BAS Deadlines (Quarterly)
        { 
          id: 1, 
          title: 'BAS Q3 (Jan-Mar)', 
          entity: 'Punjab Imports Pty Ltd',
          dueDate: `${currentYear}-04-28`,
          type: 'bas',
          status: 'pending',
          category: 'BAS'
        },
        { 
          id: 2, 
          title: 'BAS Q3 (Jan-Mar)', 
          entity: 'Punjab Fresh Pty Ltd',
          dueDate: `${currentYear}-04-28`,
          type: 'bas',
          status: 'pending',
          category: 'BAS'
        },
        { 
          id: 3, 
          title: 'BAS Q3 (Jan-Mar)', 
          entity: 'Punjab Wholesale Pty Ltd',
          dueDate: `${currentYear}-04-28`,
          type: 'bas',
          status: 'pending',
          category: 'BAS'
        },
        
        // Company Tax Returns (CTR)
        { 
          id: 4, 
          title: 'Company Tax Return FY24', 
          entity: 'Punjab Imports Pty Ltd',
          dueDate: `${currentYear}-05-15`,
          type: 'ctr',
          status: 'pending',
          category: 'CTR'
        },
        { 
          id: 5, 
          title: 'Company Tax Return FY24', 
          entity: 'Punjab Fresh Pty Ltd',
          dueDate: `${currentYear}-05-15`,
          type: 'ctr',
          status: 'pending',
          category: 'CTR'
        },
        { 
          id: 6, 
          title: 'Company Tax Return FY24', 
          entity: 'Punjab Wholesale Pty Ltd',
          dueDate: `${currentYear}-05-15`,
          type: 'ctr',
          status: 'pending',
          category: 'CTR'
        },
        
        // Personal ITR (Individual Tax Returns)
        { 
          id: 7, 
          title: 'Personal ITR FY24', 
          entity: 'Yogesh Dhawan (Director)',
          dueDate: `${currentYear}-05-15`,
          type: 'itr',
          status: 'pending',
          category: 'ITR'
        },
        { 
          id: 8, 
          title: 'Personal ITR FY24', 
          entity: 'Sahil Dhawan (Director)',
          dueDate: `${currentYear}-05-15`,
          type: 'itr',
          status: 'pending',
          category: 'ITR'
        },
        
        // Trust Returns
        { 
          id: 9, 
          title: 'Trust Tax Return FY24', 
          entity: 'Punjab Properties Trust',
          dueDate: `${currentYear}-05-15`,
          type: 'trust',
          status: 'pending',
          category: 'Trust'
        },
        { 
          id: 10, 
          title: 'Trust Tax Return FY24', 
          entity: 'Dhawan Family Trust',
          dueDate: `${currentYear}-05-15`,
          type: 'trust',
          status: 'pending',
          category: 'Trust'
        },
        
        // Superannuation
        { 
          id: 11, 
          title: 'Super Guarantee Q3', 
          entity: 'All Trading Entities',
          dueDate: `${currentYear}-04-28`,
          type: 'super',
          status: 'pending',
          category: 'Super'
        },
        
        // Payroll Tax
        { 
          id: 12, 
          title: 'Payroll Tax - March', 
          entity: 'Punjab Imports Pty Ltd',
          dueDate: `${currentYear}-04-07`,
          type: 'payroll',
          status: 'pending',
          category: 'Payroll'
        },
        
        // PAYG Withholding
        { 
          id: 13, 
          title: 'PAYG Summary Annual', 
          entity: 'All Entities',
          dueDate: `${currentYear}-08-14`,
          type: 'payg',
          status: 'pending',
          category: 'PAYG'
        },
        
        // FBT (Fringe Benefits Tax)
        { 
          id: 14, 
          title: 'FBT Return', 
          entity: 'Punjab Imports Pty Ltd',
          dueDate: `${currentYear}-05-21`,
          type: 'fbt',
          status: 'pending',
          category: 'FBT'
        },
        
        // ASIC Annual Review
        { 
          id: 15, 
          title: 'ASIC Annual Review', 
          entity: 'Punjab Imports Pty Ltd',
          dueDate: `${currentYear}-06-30`,
          type: 'asic',
          status: 'pending',
          category: 'ASIC'
        },
        { 
          id: 16, 
          title: 'ASIC Annual Review', 
          entity: 'Punjab Fresh Pty Ltd',
          dueDate: `${currentYear}-07-15`,
          type: 'asic',
          status: 'pending',
          category: 'ASIC'
        },
        
        // Workers Comp
        { 
          id: 17, 
          title: 'Workers Comp Renewal', 
          entity: 'Punjab Imports Pty Ltd',
          dueDate: `${currentYear}-06-30`,
          type: 'insurance',
          status: 'pending',
          category: 'Insurance'
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getDaysUntil = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(dateStr);
    dueDate.setHours(0, 0, 0, 0);
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getUrgencyColor = (days) => {
    if (days < 0) return 'text-red-600 bg-red-50';
    if (days <= 7) return 'text-orange-600 bg-orange-50';
    if (days <= 14) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  const getUrgencyBadge = (days) => {
    if (days < 0) return { text: 'Overdue', color: 'bg-red-100 text-red-700' };
    if (days === 0) return { text: 'Due Today', color: 'bg-red-100 text-red-700' };
    if (days <= 7) return { text: `${days}d left`, color: 'bg-orange-100 text-orange-700' };
    if (days <= 14) return { text: `${days}d left`, color: 'bg-yellow-100 text-yellow-700' };
    if (days <= 30) return { text: `${days}d left`, color: 'bg-blue-100 text-blue-700' };
    return { text: `${days}d left`, color: 'bg-green-100 text-green-700' };
  };

  const getCategoryColor = (category) => {
    const colors = {
      'BAS': 'bg-purple-100 text-purple-700',
      'CTR': 'bg-blue-100 text-blue-700',
      'ITR': 'bg-indigo-100 text-indigo-700',
      'Trust': 'bg-pink-100 text-pink-700',
      'Super': 'bg-emerald-100 text-emerald-700',
      'Payroll': 'bg-orange-100 text-orange-700',
      'PAYG': 'bg-cyan-100 text-cyan-700',
      'FBT': 'bg-amber-100 text-amber-700',
      'ASIC': 'bg-slate-100 text-slate-700',
      'Insurance': 'bg-teal-100 text-teal-700',
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  const urgentCount = deadlines.filter(d => getDaysUntil(d.dueDate) <= 7).length;

  return (
    <Card className="h-full" data-testid="financial-deadlines-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Calendar className="h-5 w-5 text-red-600" />
            Financial Deadlines
            {urgentCount > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded-full">
                {urgentCount} urgent
              </span>
            )}
          </CardTitle>
          {expandable && expandPath && (
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate(expandPath)}
              className="text-slate-500 hover:text-slate-700"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="animate-pulse space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-14 bg-slate-100 rounded-lg"></div>
            ))}
          </div>
        ) : deadlines.length === 0 ? (
          <div className="text-center py-6 text-slate-500">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-green-500" />
            <p className="text-sm">No upcoming deadlines</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {deadlines.map((deadline) => {
              const days = getDaysUntil(deadline.dueDate);
              const badge = getUrgencyBadge(days);
              const categoryColor = getCategoryColor(deadline.category);
              return (
                <div 
                  key={deadline.id} 
                  className={`p-3 rounded-lg border ${days <= 7 ? 'border-orange-200 bg-orange-50/50' : 'border-slate-200'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${categoryColor}`}>
                          {deadline.category}
                        </span>
                      </div>
                      <p className="font-medium text-sm text-slate-800 truncate">{deadline.title}</p>
                      <p className="text-xs text-slate-500 truncate">{deadline.entity}</p>
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${badge.color}`}>
                      {badge.text}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 mt-1.5 text-xs text-slate-500">
                    <Clock className="h-3 w-3" />
                    <span>{new Date(deadline.dueDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
